import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaBriefcase, FaUserAlt } from 'react-icons/fa';
import { getUserById, getAdminProperties } from '../../../api/services/adminService';
import { getPropertyRentStatus } from '../../../api/services/billingService';
import UserAvatar from './UserAvatar';
import { Badge, EmptyState, LoadingSkeleton, Alert } from './AdminUI';
import './PropertyDetails.css'; // Leverage existing admin detail styles

const AgentDetails = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();

  const [agent, setAgent] = useState(null);
  const [properties, setProperties] = useState([]);
  const [rentStatuses, setRentStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const userRes = await getUserById(agentId);
      setAgent(userRes?.data || userRes);

      // Fetch properties filtered by agentId using our newly extended backend parameter!
      const propsRes = await getAdminProperties({ agentId, size: 100 });
      const propsList = propsRes?.data?.content || propsRes?.content || [];
      setProperties(propsList);

      // Load rent statuses for all properties in parallel
      const now = new Date();
      const statusPromises = propsList.map(async (prop) => {
        try {
          const statusRes = await getPropertyRentStatus(prop.id, { year: now.getFullYear(), month: now.getMonth() + 1 });
          return { id: prop.id, data: statusRes?.data || statusRes };
        } catch (e) {
          console.error(`Failed to load rent status for property ${prop.id}`, e);
          return { id: prop.id, data: null };
        }
      });

      const resolved = await Promise.all(statusPromises);
      const statusMap = {};
      resolved.forEach((item) => {
        if (item.data) {
          statusMap[item.id] = item.data;
        }
      });
      setRentStatuses(statusMap);

    } catch (e) {
      setError(e?.message || 'Failed to load agent details');
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Aggregate Stats
  const stats = useMemo(() => {
    let totalUnits = 0;
    let occupiedUnits = 0;
    let expectedRent = 0;

    properties.forEach((prop) => {
      const rentData = rentStatuses[prop.id];
      if (rentData) {
        totalUnits += rentData.items ? rentData.items.length : (prop.unitCount || 0);
        occupiedUnits += rentData.items ? rentData.items.filter(i => i.paymentStatus === 'PAID' || i.paymentStatus === 'PARTIAL').length : (prop.occupiedUnits || 0);
        expectedRent += rentData.totalExpected || 0;
      } else {
        totalUnits += prop.unitCount || 0;
        occupiedUnits += prop.occupiedUnits || 0;
        expectedRent += prop.totalMonthlyRentExpected || 0;
      }
    });

    return {
      totalUnits,
      occupiedUnits,
      expectedRent,
      vacantUnits: Math.max(0, totalUnits - occupiedUnits),
    };
  }, [properties, rentStatuses]);

  // Filtered Properties
  const filteredProperties = useMemo(() => {
    return properties.filter((prop) => {
      // Search filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchesName = prop.name?.toLowerCase().includes(q);
        const matchesAddress = prop.address?.toLowerCase().includes(q);
        if (!matchesName && !matchesAddress) return false;
      }
      // Visibility filter
      if (visibilityFilter !== 'ALL') {
        const isPub = visibilityFilter === 'PUBLISHED';
        if (prop.published !== isPub) return false;
      }
      // Status filter
      if (statusFilter !== 'ALL') {
        const isActive = statusFilter === 'ACTIVE';
        if (prop.isActive !== isActive) return false;
      }
      return true;
    });
  }, [properties, searchQuery, visibilityFilter, statusFilter]);

  const verifBadge = (status) => {
    if (!status || status === 'NONE') return <Badge status="neutral">Not verified</Badge>;
    if (status === 'APPROVED' || status === 'VERIFIED') return <Badge status="success">Verified</Badge>;
    if (status === 'REJECTED') return <Badge status="danger">Rejected</Badge>;
    return <Badge status="warning">Pending</Badge>;
  };

  const formatMoney = (val) => {
    return `KES ${Number(val || 0).toLocaleString('en-KE', { maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="pd-container">
        <button type="button" className="pd-back" onClick={() => navigate('/system-admin/agents')}>
          <FaArrowLeft /> Back to Agents
        </button>
        <LoadingSkeleton lines={8} />
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="pd-container">
        <button type="button" className="pd-back" onClick={() => navigate('/system-admin/agents')}>
          <FaArrowLeft /> Back to Agents
        </button>
        <Alert type="error">{error || 'Agent not found.'}</Alert>
      </div>
    );
  }

  return (
    <div className="pd-container">
      <button type="button" className="pd-back" onClick={() => navigate('/system-admin/agents')}>
        <FaArrowLeft /> Back to Agents
      </button>

      {/* Header Banner */}
      <div className="pd-header">
        <div className="pd-header-media" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ecf0f9' }}>
          <UserAvatar
            profilePhotoPath={agent.profilePhotoPath}
            firstName={agent.firstName}
            lastName={agent.lastName}
            size={70}
          />
        </div>
        <div className="pd-header-body">
          <div className="pd-header-title">
            <h1>{agent.firstName} {agent.lastName}</h1>
            <Badge status={agent.isActive ? 'success' : 'danger'}>
              {agent.isActive ? 'Active Agent' : 'Suspended'}
            </Badge>
          </div>
          <p className="pd-header-sub">{agent.email} • {agent.phone || 'No phone number'}</p>
        </div>
      </div>

      {/* Stats Summary Strip */}
      <div className="pd-stats" style={{ marginTop: 20 }}>
        <div className="pd-stat">
          <span className="pd-stat-value">{properties.length}</span>
          <span className="pd-stat-label">Properties managed</span>
        </div>
        <div className="pd-stat">
          <span className="pd-stat-value">{stats.totalUnits}</span>
          <span className="pd-stat-label">Total units managed</span>
        </div>
        <div className="pd-stat">
          <span className="pd-stat-value">{stats.occupiedUnits}</span>
          <span className="pd-stat-label">Occupied units</span>
        </div>
        <div className="pd-stat">
          <span className="pd-stat-value">{stats.vacantUnits}</span>
          <span className="pd-stat-label">Vacant units</span>
        </div>
        <div className="pd-stat">
          <span className="pd-stat-value">{formatMoney(stats.expectedRent)}</span>
          <span className="pd-stat-label">Total rent expected (this mo)</span>
        </div>
      </div>

      <div className="pd-columns" style={{ marginTop: 24 }}>
        {/* Left column: managed properties table */}
        <div className="pd-col-main" style={{ flex: 2 }}>
          <div className="admin-card" style={{ padding: 20, marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
              <h2 className="admin-card-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <FaBriefcase /> Managed Properties ({filteredProperties.length})
              </h2>
            </div>

            {/* Filter Panel */}
            <div className="filter-panel" style={{ padding: 14, background: '#f9fafb', borderRadius: 10, marginBottom: 16, border: '1px solid #e5e7eb' }}>
              <div className="filter-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                <div className="filter-item">
                  <label className="filter-label" style={{ fontSize: 10 }}>Search properties</label>
                  <input
                    type="text"
                    className="filter-input"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search name or address..."
                    style={{ padding: 8, fontSize: 12 }}
                  />
                </div>
                <div className="filter-item">
                  <label className="filter-label" style={{ fontSize: 10 }}>Visibility</label>
                  <select
                    className="filter-select"
                    value={visibilityFilter}
                    onChange={(e) => setVisibilityFilter(e.target.value)}
                    style={{ padding: 8, fontSize: 12 }}
                  >
                    <option value="ALL">All</option>
                    <option value="PUBLISHED">Published Only</option>
                    <option value="UNPUBLISHED">Unpublished Only</option>
                  </select>
                </div>
                <div className="filter-item">
                  <label className="filter-label" style={{ fontSize: 10 }}>Status</label>
                  <select
                    className="filter-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{ padding: 8, fontSize: 12 }}
                  >
                    <option value="ALL">All</option>
                    <option value="ACTIVE">Active Only</option>
                    <option value="BLOCKED">Blocked Only</option>
                  </select>
                </div>
              </div>
            </div>

            {filteredProperties.length === 0 ? (
              <EmptyState icon="🏢" title="No managed properties found" text="Try clearing search query or filters." />
            ) : (
              <div className="admin-table-wrap" style={{ border: '1px solid #e5e7eb', borderRadius: 12 }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Property</th>
                      <th>Expected Rent</th>
                      <th>Occupancy</th>
                      <th>Landlord / Owner</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProperties.map((prop) => {
                      const rentData = rentStatuses[prop.id];
                      const propExpected = rentData ? rentData.totalExpected : (prop.totalMonthlyRentExpected || 0);
                      const propTotalUnits = rentData && rentData.items ? rentData.items.length : (prop.unitCount || 0);
                      const propOccupiedUnits = rentData && rentData.items ? rentData.items.filter(i => i.paymentStatus === 'PAID' || i.paymentStatus === 'PARTIAL').length : (prop.occupiedUnits || 0);
                      const propOwnerEmail = prop.contacts?.email || '—';

                      return (
                        <tr key={prop.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/system-admin/properties/${prop.id}`)}>
                          <td>
                            <div style={{ fontWeight: 600, color: '#02236A' }}>{prop.name}</div>
                            <div style={{ fontSize: 11, color: '#6b7280' }}>{prop.address || 'No address listed'}</div>
                          </td>
                          <td style={{ fontWeight: 600 }}>{formatMoney(propExpected)}</td>
                          <td>
                            <span style={{ fontWeight: 500 }}>{propOccupiedUnits} / {propTotalUnits}</span>
                            <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 4 }}>occupied</span>
                          </td>
                          <td style={{ fontSize: 13, color: '#4b5563' }}>{propOwnerEmail}</td>
                          <td>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <Badge status={prop.isActive ? 'success' : 'danger'}>{prop.isActive ? 'Active' : 'Blocked'}</Badge>
                              <Badge status={prop.published ? 'info' : 'neutral'}>{prop.published ? 'Published' : 'Draft'}</Badge>
                            </div>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="admin-btn admin-btn-outline"
                              style={{ padding: '6px 12px', fontSize: 11 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/system-admin/properties/${prop.id}`);
                              }}
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Right column: agent profile information */}
        <div className="pd-col-side" style={{ flex: 1 }}>
          <div className="admin-card" style={{ padding: 20, marginBottom: 20 }}>
            <h2 className="admin-card-title" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <FaUserAlt /> Agent Profile
            </h2>
            <div className="pd-info-list" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="pd-info-item" style={{ display: 'flex', flexDirection: 'column', borderBottom: '1px solid #f3f4f6', paddingBottom: 8 }}>
                <span className="pd-info-label" style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>National ID</span>
                <span className="pd-info-value" style={{ fontWeight: 600, fontSize: 13.5 }}>{agent.idNumber || '—'}</span>
              </div>
              <div className="pd-info-item" style={{ display: 'flex', flexDirection: 'column', borderBottom: '1px solid #f3f4f6', paddingBottom: 8 }}>
                <span className="pd-info-label" style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>KYC Verification</span>
                <span className="pd-info-value" style={{ fontWeight: 600, fontSize: 13.5 }}>{verifBadge(agent.kycStatus)}</span>
              </div>
              <div className="pd-info-item" style={{ display: 'flex', flexDirection: 'column', borderBottom: '1px solid #f3f4f6', paddingBottom: 8 }}>
                <span className="pd-info-label" style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>Joined Date</span>
                <span className="pd-info-value" style={{ fontWeight: 600, fontSize: 13.5 }}>{new Date(agent.createdAt).toLocaleDateString()}</span>
              </div>
              <div className="pd-info-item" style={{ display: 'flex', flexDirection: 'column', paddingBottom: 8 }}>
                <span className="pd-info-label" style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>Last Login</span>
                <span className="pd-info-value" style={{ fontWeight: 600, fontSize: 13.5 }}>{agent.lastLogin ? new Date(agent.lastLogin).toLocaleString() : 'Never logged in'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentDetails;
